import React, { useState, useEffect, useCallback } from 'react';
import { useLocalData } from '../../../hooks/useLocalData';
import { Alert, Button, Col, Row, Select } from 'antd';
import { useUserContext } from '../../../contexts/UserContext';
import { useForm } from 'antd/es/form/Form';
import AddProductForm from '../../../components/product/AddProduct';
import { trimValues } from '../../../utils/trimValues';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { Plus, XSquare } from 'react-bootstrap-icons';
import ProductList from '../../../components/product/ProductList';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';
import { useTranslation } from 'react-i18next';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import { getPresentationList } from './getData';
import { productService } from '../../../api/services/productService';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { normalize } from '../../../utils/normalizeWords';
import dayjs from 'dayjs';
import { PresentationsDisplay } from '../components/PresentationsDisplay';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { ActiveFilterEnum } from '../../../enums/ActiveFilterEnum';
import { PermissionEnum } from '../../../enums/PermissionEnum';
import { usePermissions } from '../../../hooks/usePermissions';
import { localDataService } from '../../../services/localDataService';

const initialProduct = {
  name: '',
  mainCategory: '',
  subCategory: '',
  unit: '',
  basketId: undefined,
  presentation: '',
  brand: '',
};

const ProductView: React.FC = () => {
  const [addProductForm] = useForm();
  const { t } = useTranslation(['product', 'form']);

  const [newProduct, setNewProduct] = useState(initialProduct);
  const [authStatus, setAuthStatus] = useState<{ isAuthenticated: boolean; token: string | null }>({
    isAuthenticated: false,
    token: null,
  });

  const [isProductModalOpen, setIsProductModalOpen] = useState<boolean>(false);

  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedProductName, setSelectedProductName] = useState<string>('');

  const { successNotification, errorNotification } = useNotificationContext();

  const { IsAuthenticated, getUserToken } = useUserContext();

  const [isConfirmationOpen, setIsConfirmationOpen] = useState<boolean>(false);

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);

  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState<boolean>(false);

  const [basketOptions, setBasketOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);

  const [selectedProductCategories, setSelectedProductCategories] = useState<string[]>([]);
  const [selectedBaskets, setSelectedBaskets] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<ActiveFilterEnum>(ActiveFilterEnum.ACTIVE);

  const [productCategories, setProductCategories] = useState<any[]>([]);
  const [baskets, setBaskets] = useState<any[]>([]);

  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);

  const [isEditDataLoading, setIsEditDataLoading] = useState<boolean>(false);
  const [isConfirmClicked, setIsConfirmClicked] = useState<boolean>(false);
  const [isRestoreConfirmationOpen, setIsRestoreConfirmationOpen] = useState<boolean>(false);

  const { isOnline } = useIsOnlineContext();
  const { hasPermission } = usePermissions();
  const canCreatePresentations = hasPermission(PermissionEnum.CREATE_PRESENTATION);

  const getBasketsData = async () => {
    try {
      const allBaskets = await referenceDataSyncService.getBaskets();
      const basketOptionArr = allBaskets
        ?.map((basket) => ({
          label: basket.name,
          id: basket.id,
          value: basket.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setBasketOptions(basketOptionArr);
    } catch (error) {
      console.error('Error loading baskets:', error);
    }
  };

  const {
    data: products,
    loading,
    create,
    delete: deleteProduct,
    update,
    updateLocalData,
    query: queryLocalData,
  } = useLocalData({
    entity: 'presentation',
    autoSync: true,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [productData, setProductData] = useState<any[]>(products);
  const [productFilterData, setProductFilterData] = useState<any[]>(products);
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalProducts, setTotalProducts] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    const token = getUserToken();
    const isAuth = IsAuthenticated();
    setAuthStatus({ isAuthenticated: isAuth, token });
  }, [IsAuthenticated, getUserToken]);

  useEffect(() => {
    getAllProductList();
    getBasketsData();
  }, []);

  useEffect(() => {
    const handleSyncCompleted = (event: any) => {
      const { entity, id } = event.detail;
      if (entity === 'presentation') {
        setProductData((prevProducts) =>
          prevProducts.map((product) =>
            product.refId === id ? { ...product, synced: 1 } : product
          )
        );
      }
    };

    window.addEventListener('sync-completed', handleSyncCompleted);
    return () => {
      window.removeEventListener('sync-completed', handleSyncCompleted);
    };
  }, []);

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);
    try {
      const categoriesData = await referenceDataSyncService.getProductCategories();
      setProductCategories(
        categoriesData.sort((a, b) => a.mainCategory.localeCompare(b.mainCategory))
      );

      const basketsData = await referenceDataSyncService.getBaskets();
      setBaskets(basketsData.sort((a, b) => a.name.localeCompare(b.name)));

      const productsData = await referenceDataSyncService.getPresentations();
      setProductFilterData(productsData);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, [errorNotification]);

  useEffect(() => {
    fetchFilterData();
  }, [fetchFilterData]);

  /* const getAllProductList = async (page: number = currentPage) => {
    setIsLoadingProducts(true);
    try {
      const paginationParams = {
        page,
        limit: pageSize,
      };

      const response = await fetcher.get(
        'product/list',
        paginationParams,
        {},
        ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
      );

      if (response.statusText === 'SUCCESS' && response.data?.data) {
        const products = response.data.data;
        const total = response.data.total || 0;
        setProductData(products);
        setTotalProducts(total);
        setCurrentPage(page);
        enrichProductCategoryData(products);
      } else {
        console.error('Failed to fetch products from backend:', response);
      }
    } catch (error) {
      console.error('Error fetching products from backend:', error);
    } finally {
      setIsLoadingProducts(false);
    }
  }; */

  const getAllProductList = useCallback(
    async (page: number = currentPage, filters?: any, customPageSize?: number) => {
      const pageLimit = customPageSize !== undefined ? customPageSize : pageSize;

      if (!isOnline) {
        setIsLoadingProducts(true);
        try {
          const currentFilters = filters || {
            productCategories: selectedProductCategories,
            baskets: selectedBaskets,
            products: selectedProducts,
            brands: selectedBrands,
            status: statusFilter,
          };

          const localFilters: Record<string, any> = {};
          if (currentFilters?.productCategories?.length) {
            localFilters.productCategoryId = currentFilters.productCategories;
          }
          if (currentFilters?.baskets?.length) {
            localFilters.basketIds = currentFilters.baskets;
          }
          if (currentFilters?.products?.length) {
            localFilters.refId = currentFilters.products;
          }
          if (currentFilters?.brands?.length) {
            localFilters.brand = currentFilters.brands;
          }

          const localResult = await queryLocalData('presentation', {
            page,
            limit: pageLimit,
            sortBy: 'name',
            sortOrder: 'asc',
            filters: Object.keys(localFilters).length > 0 ? localFilters : undefined,
          });
          enrichProductCategoryData(localResult.data);
          setTotalProducts(localResult.total);
          setCurrentPage(page);
        } catch (error) {
          console.error('Error fetching offline products:', error);
        } finally {
          setIsLoadingProducts(false);
        }
        return;
      }

      setIsLoadingProducts(true);
      try {
        const paginationParams = {
          page,
          limit: pageLimit,
        };

        const currentFilters = filters || {
          productCategories: selectedProductCategories,
          baskets: selectedBaskets,
          products: selectedProducts,
          brands: selectedBrands,
          status: statusFilter,
        };

        const filterParams: any = {
          ...paginationParams,
        };

        if (
          currentFilters?.productCategories &&
          Array.isArray(currentFilters?.productCategories) &&
          currentFilters?.productCategories.length > 0
        ) {
          filterParams.productCategoryIds = currentFilters?.productCategories;
        }

        if (
          currentFilters?.baskets &&
          Array.isArray(currentFilters?.baskets) &&
          currentFilters?.baskets.length > 0
        ) {
          filterParams.basketIds = currentFilters?.baskets;
        }

        if (
          currentFilters?.products &&
          Array.isArray(currentFilters?.products) &&
          currentFilters?.products.length > 0
        ) {
          filterParams.productRefIds = currentFilters?.products;
        }

        if (
          currentFilters?.brands &&
          Array.isArray(currentFilters?.brands) &&
          currentFilters?.brands.length > 0
        ) {
          filterParams.brands = currentFilters?.brands;
        }

        if (currentFilters?.status) {
          filterParams.status = currentFilters.status;
        }

        /* const response = await fetcher.get(
          'product/list',
          filterParams,
          {},
          ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
        ); */

        const response = await getPresentationList(filterParams);

        if (response.statusText === 'SUCCESS') {
          const products = response.data?.data?.data || response.data?.data || response.data || [];
          const total = response.data?.data?.total || response.data?.total || 0;
          setProductData(products);
          setTotalProducts(total);
          setCurrentPage(page);
          enrichProductCategoryData(products);
        } else {
          console.error(t('productFetchError'), response);
        }
      } catch (error) {
        console.error('Failed to fetch products from backend:', error);
      } finally {
        setIsLoadingProducts(false);
      }
    },
    [
      pageSize,
      errorNotification,
      t,
      isOnline,
      selectedProductCategories,
      selectedBaskets,
      selectedProducts,
      selectedBrands,
      statusFilter,
      currentPage,
    ]
  );

  useEffect(() => {
    const filters = {
      productCategories: selectedProductCategories,
      baskets: selectedBaskets,
      products: selectedProducts,
      brands: selectedBrands,
      status: statusFilter,
    };

    const hasFilters =
      selectedProductCategories.length > 0 ||
      selectedBaskets.length > 0 ||
      selectedProducts.length > 0 ||
      selectedBrands.length > 0 ||
      statusFilter !== 'active';

    if (hasFilters) {
      getAllProductList(1, filters);
      setEnableClearFiltersBtn(true);
    } else {
      getAllProductList(1, { status: 'active' });
      setEnableClearFiltersBtn(false);
    }
  }, [selectedProductCategories, selectedBaskets, selectedProducts, selectedBrands, statusFilter]);

  const clearAllFilters = () => {
    setSelectedProductCategories([]);
    setSelectedBaskets([]);
    setSelectedProducts([]);
    setSelectedBrands([]);
    setStatusFilter('active');
    getAllProductList(1, { status: 'active' });
  };

  useEffect(() => {
    if (!isOnline) {
      setStatusFilter('active');
    }
  }, [isOnline]);

  const enrichSingleProductCategory = async (product: any) => {
    let productCategoryData = null;

    if (product?.productCategoryId) {
      const categoryArray = await referenceDataSyncService.getProductCategoryDataById(
        product.productCategoryId.toString()
      );
      productCategoryData = categoryArray && categoryArray.length > 0 ? categoryArray[0] : null;
    }

    return {
      ...product,
      productCategory: productCategoryData,
    };
  };

  const enrichProductCategoryData = async (products: any[]) => {
    try {
      const productWithCategoryData = await Promise.all(products.map(enrichSingleProductCategory));
      setProductData(productWithCategoryData);
    } catch (error) {
      console.error('Error enriching products with category data:', error);
      setProductData(products);
    }
  };

  const handlePageChange = (page: number, size?: number) => {
    const newPageSize = size && size !== pageSize ? size : pageSize;

    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
    const filters = {
      productCategories: selectedProductCategories,
      baskets: selectedBaskets,
      products: selectedProducts,
      brands: selectedBrands,
      status: statusFilter,
    };

    // Call getAllProductList with updated page size
    getAllProductList(page, filters, newPageSize);
  };

  const handleCancel = () => {
    addProductForm.resetFields();
    setNewProduct(initialProduct);
    setIsProductModalOpen(false);
  };

  const getConfirmation = async () => {
    await addProductForm.validateFields();
    setIsConfirmationOpen(true);
  };

  function convertDayjsToEpoch(value: any): any {
    if (dayjs.isDayjs(value)) {
      return value.valueOf();
    }

    if (Array.isArray(value)) {
      return value.map(convertDayjsToEpoch);
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, convertDayjsToEpoch(v)]));
    }

    return value;
  }

  const handleCreate = async () => {
    // To disable the confirm button to avoid multiple clicks
    setIsConfirmClicked(true);

    const productFormData = await addProductForm.getFieldsValue();

    const trimmedValues = trimValues(productFormData);
    addProductForm.setFieldsValue(trimmedValues);
    await addProductForm.validateFields();

    let allProducts = await referenceDataSyncService.getPresentations();

    if (isOnline) {
      try {
        const response = await getPresentationList({
          status: 'all',
          search: trimmedValues.name || trimmedValues.mainProductName || '',
        });
        if (response.statusText === 'SUCCESS') {
          allProducts =
            response.data?.data?.data || response.data?.data || response.data || allProducts;
        }
      } catch (error) {
        console.error('Error fetching products for duplicate check:', error);
      }
    }

    let duplicateProduct: any = null;

    const isDuplicate = allProducts.some((product) => {
      if (isEdit && product.refId === selectedProductId) return false;

      // Extract IDs or Custom Strings from form values
      const formMainProductId = Number(trimmedValues.mainProductName); // holds ID

      const isCustomBrand = trimmedValues.brand === 'Other';
      const formBrandId = isCustomBrand ? null : Number(trimmedValues.brand);
      const formCustomBrandStr = isCustomBrand
        ? (trimmedValues.customBrand || '').trim().toLowerCase()
        : '';

      const isCustomPresentation = trimmedValues.presentation === 'Other';
      const formPresentationId = isCustomPresentation ? null : Number(trimmedValues.presentation);
      const formCustomPresentationStr = isCustomPresentation
        ? (trimmedValues.customPresentation || '').trim().toLowerCase()
        : '';

      // Check Main Product Match
      const prodMainProductId = product.mainProductId || product.mainProduct?.id;
      const isMainProductMatch = prodMainProductId === formMainProductId;

      // Check Brand Match
      let isBrandMatch = false;
      const prodBrandId = product.brandId || product.productBrand?.id;
      if (formBrandId) {
        isBrandMatch = prodBrandId === formBrandId;
      } else {
        const prodBrandStr = (product.brand || product.productBrand?.name || '')
          .trim()
          .toLowerCase();
        isBrandMatch = prodBrandStr === formCustomBrandStr;
      }

      // Check Presentation Match
      let isPresentationMatch = false;
      const prodPresentationId = product.presentationId || product.productPresentation?.id;
      if (formPresentationId) {
        isPresentationMatch = prodPresentationId === formPresentationId;
      } else {
        const prodPresentationStr = (
          product.presentation ||
          product.productPresentation?.name ||
          ''
        )
          .trim()
          .toLowerCase();
        isPresentationMatch = prodPresentationStr === formCustomPresentationStr;
      }

      const match = isMainProductMatch && isBrandMatch && isPresentationMatch;
      if (match) {
        duplicateProduct = product;
      }
      return match;
    });

    if (isDuplicate) {
      if (duplicateProduct?.isActive === false) {
        errorNotification(t('inactiveDuplicatePresentationError'));
      } else {
        errorNotification(t('duplicatePresentationError'));
      }
      setIsConfirmClicked(false);
      setIsConfirmationOpen(false);
      return;
    }

    const isBarcodeDuplicate = allProducts.some((product) => {
      if (product.refId === selectedProductId) return false; // Ignore itself in edit mode

      // Only check if both have barcodes
      if (!product.barcode || !trimmedValues.barcode) return false;

      return product.barcode.trim() === trimmedValues.barcode.trim();
    });

    if (isBarcodeDuplicate) {
      errorNotification(t('barcodeAlreadyExists'));
      setIsConfirmClicked(false);
      setIsConfirmationOpen(false);
      return;
    }

    // Close the popups to avoid multiple clicks
    setIsConfirmationOpen(false);
    setIsProductModalOpen(false);
    setIsLoadingProducts(true);

    setNewProduct(productFormData);

    // productCategoryId comes from the form field (set by auto-fill or edit loading)
    const productCategoryId = trimmedValues?.productCategoryId
      ? parseInt(trimmedValues.productCategoryId)
      : trimmedValues?.productCategory
        ? parseInt(trimmedValues.productCategory)
        : null;

    const basketIds = trimmedValues?.basketIds || [];

    const convertedCustomFields = convertDayjsToEpoch(trimmedValues.customFields);

    // Resolve strings for 'Other', or keep null for IDs
    const brandString = trimmedValues?.brand === 'Other' ? trimmedValues?.customBrand : null;
    const brandIdValue = trimmedValues?.brand === 'Other' ? null : Number(trimmedValues?.brand);

    const presentationString =
      trimmedValues?.presentation === 'Other' ? trimmedValues?.customPresentation : null;
    const presentationIdValue =
      trimmedValues?.presentation === 'Other' ? null : Number(trimmedValues?.presentation);

    // Full body for local storage (includes frontend-only fields)
    const body = {
      name: trimmedValues?.name,
      // Fallback to label if it's an existing ID option
      brand: brandString || trimmedValues?.brandLabel,
      presentation: presentationString || trimmedValues?.presentationLabel,
      basketIds: basketIds,
      barcode: trimmedValues?.barcode?.trim() ? trimmedValues.barcode.trim() : null,
      isActive: true,
      customFields: convertedCustomFields ? convertedCustomFields : null,
      equivalentAmount:
        trimmedValues?.equivalentAmount !== undefined &&
          trimmedValues.equivalentAmount !== null &&
          trimmedValues.equivalentAmount !== ''
          ? Number(trimmedValues.equivalentAmount)
          : null,
      mainProductName: trimmedValues?.mainProductLabel,
      mainProductId: Number(trimmedValues?.mainProductName),
      productCategoryId: productCategoryId,
      unit: trimmedValues?.unit,
    };

    // syncPayload contains ONLY fields accepted by CreateProductDto on the backend
    const syncPayload = {
      name: body.name,
      brand: brandString,
      brandId: brandIdValue,
      presentation: presentationString,
      presentationId: presentationIdValue,
      barcode: body.barcode,

      customFields: body.customFields,
      equivalentAmount: body.equivalentAmount,
      unit: body.unit,
      mainProductId: body.mainProductId,
    };

    // setIsConfirmationOpen(false);
    // setIsProductModalOpen(false);

    if (isEdit) {
      try {
        const result = await update(selectedProductId, body, syncPayload);

        if (result) {
          const updatedProductData = {
            ...result,
            ...body,
            updateTime: Date.now(),
            createTime: Date.now(),
            synced: 0,
          };

          const enrichedProduct = await enrichSingleProductCategory(updatedProductData);

          setProductData((prevProducts) =>
            prevProducts.map((product) =>
              product.refId === selectedProductId ? enrichedProduct : product
            )
          );

          updateLocalData((prevProducts) =>
            prevProducts.map((product) =>
              product.refId === selectedProductId
                ? { ...product, ...body, updateTime: Date.now() }
                : product
            )
          );

          setNewProduct(initialProduct);
          addProductForm.resetFields();
          successNotification(t('productUpdateSuccess'));
          // setIsConfirmationOpen(false);
          // setIsProductModalOpen(false);
        }
      } catch (error) {
        console.error('Failed to create product:', error);
        // setIsProductModalOpen(false);
        // setIsConfirmationOpen(false);
        errorNotification(t('productUpdateFail'));
      } finally {
        setIsLoadingProducts(false);
        setIsConfirmClicked(false);
      }
    } else {
      try {
        const result = await create(body, syncPayload);

        if (result) {
          const newProductData = {
            ...result,
            priceCount: 0,
            createTime: Date.now(),
            updateTime: Date.now(),
            synced: 0,
          };

          const enrichedProduct = await enrichSingleProductCategory(newProductData);

          setProductData((prevProducts) => [enrichedProduct, ...prevProducts]);
          setTotalProducts((prevTotal) => prevTotal + 1);

          updateLocalData((prevProducts) => [newProductData, ...prevProducts]);

          setNewProduct(initialProduct);
          addProductForm.resetFields();
          successNotification(t('productCreateSuccess'));
          // setIsConfirmationOpen(false);
          // setIsProductModalOpen(false);
        }
      } catch (error) {
        console.error('Failed to create product:', error);
        // setIsProductModalOpen(false);
        // setIsConfirmationOpen(false);
        errorNotification(t('productCreateFail'));
      } finally {
        setIsLoadingProducts(false);
        setIsConfirmClicked(false);
      }
    }
  };

  const getDeleteConfirmation = (id: string) => {
    const product = productData.find((p) => p.refId === id);

    if (product && product.basketIds && product.basketIds.length > 0) {
      errorNotification(t('presentationAssociatedWithBasketDeleteRestriction'));
      return;
    }

    if (product && product.priceCount > 0) {
      errorNotification(t('deleteRestrictionError'));
      return;
    }
    setIsDeleteConfirmationOpen(true);
    setSelectedProductId(id);
  };

  const getPresentationDetails = (id: number, name: string) => {
    setIsPresentationModalOpen(true);
    setSelectedProductId(id.toString());
    setSelectedProductName(name);
  };

  const handleDelete = async () => {
    if (!isOnline) {
      setIsDeleteConfirmationOpen(false);
      errorNotification(t('offlineDeleteInfoTitle'));
      return;
    }

    try {
      const product = productData.find((p) => p.refId === selectedProductId);
      if (!product || !product.id) {
        errorNotification(t('productDeleteFail') || 'Product not found');
        return;
      }

      // To disable the confirm button and close the confirmation popup to avoid multiple clicks
      setIsConfirmClicked(true);
      setIsDeleteConfirmationOpen(false);
      setIsLoadingProducts(true);

      await productService.deleteProduct(product.id, product.refId);

      if (statusFilter === 'active') {
        setProductData((prevProducts) =>
          prevProducts.filter((product) => product.refId !== selectedProductId)
        );
        setTotalProducts((prevTotal) => prevTotal - 1);
      } else {
        setProductData((prevProducts) =>
          prevProducts.map((product) =>
            product.refId === selectedProductId ? { ...product, isActive: false } : product
          )
        );
      }

      setIsDeleteConfirmationOpen(false);
      successNotification(t('productDeleteSuccess'));
    } catch (error) {
      console.error('Failed to delete product:', error);
      setIsDeleteConfirmationOpen(false);
      errorNotification(t('productDeleteFail'));
    } finally {
      setIsConfirmClicked(false);
      setIsLoadingProducts(false);
    }
  };

  const getRestoreConfirmation = (id: string, numericId: number) => {
    setIsRestoreConfirmationOpen(true);
    setSelectedProductId(String(numericId));
  };

  const cancelRestoreConfirmation = () => {
    setIsRestoreConfirmationOpen(false);
  };

  const handleRestore = async () => {
    try {
      setIsConfirmClicked(true);
      setIsRestoreConfirmationOpen(false);
      setIsLoadingProducts(true);

      await productService.restoreProduct(Number(selectedProductId));
      successNotification(t('productRestoreSuccess'));

      const filters = {
        productCategories: selectedProductCategories,
        baskets: selectedBaskets,
        products: selectedProducts,
        brands: selectedBrands,
        status: statusFilter,
      };

      await getAllProductList(currentPage, filters);
    } catch (error: any) {
      console.error('Failed to restore product:', error);
      errorNotification(error?.message || t('productRestoreFail'));
    } finally {
      setIsConfirmClicked(false);
      setIsLoadingProducts(false);
    }
  };

  const convertEpochToDayjs = async (productData: any, categoryFields: any) => {
    return categoryFields.reduce(
      (acc: any, catField: any) => {
        const rawValue = productData[catField.key];

        let value;

        if (rawValue !== undefined) {
          value = catField.dataType === 'date' ? dayjs(rawValue) : rawValue;
        } else {
          value = null;
        }

        acc[catField.key] = value;
        return acc;
      },
      {} as Record<string | number, any>
    );
  };

  const handleEdit = async (id: string) => {
    try {
      setSelectedProductId(id);
      setIsProductModalOpen(true);
      setIsEditDataLoading(true);

      const localProduct = productData.find((product) => product?.refId === id);
      if (!localProduct) {
        errorNotification('Product not found');
        return;
      }

      const allLocalProducts = await referenceDataSyncService.getPresentations();
      const selectedProductData =
        allLocalProducts.find((p) => p.refId === localProduct.refId) || localProduct;

      const formValues: any = {
        ...selectedProductData,
        // The mainProductName form field now expects the ID
        mainProductName:
          selectedProductData?.mainProductId ||
          localProduct?.mainProductId ||
          selectedProductData.mainProduct?.id,
        mainProductLabel:
          localProduct.mainProductName ||
          selectedProductData.mainProductName ||
          selectedProductData.name,
        brand:
          selectedProductData?.productBrand?.id ||
          selectedProductData?.brandId ||
          selectedProductData.brand,
        brandLabel:
          selectedProductData?.productBrand?.name ||
          selectedProductData?.brand ||
          localProduct?.brand,
        presentation:
          selectedProductData?.productPresentation?.id ||
          selectedProductData?.presentationId ||
          selectedProductData.presentation,
        presentationLabel:
          selectedProductData?.productPresentation?.name ||
          selectedProductData?.presentation ||
          localProduct?.presentation,
        mainCategory: selectedProductData?.productCategory?.mainCategory,
        productCategoryId: selectedProductData?.productCategoryId,
        productCategory: selectedProductData?.productCategoryId
          ? selectedProductData.productCategoryId.toString()
          : undefined,
        mainProductId:
          selectedProductData?.mainProductId ||
          localProduct?.mainProductId ||
          selectedProductData.mainProduct?.id,
        // Set unit from the local response (comes from mainProduct.unit)
        unit: selectedProductData?.unit || localProduct?.unit,
        baskets: Array.isArray(selectedProductData?.basketIds)
          ? selectedProductData.basketIds
          : undefined,
        basketIds: Array.isArray(selectedProductData?.basketIds)
          ? selectedProductData.basketIds
          : undefined,
        customFields: null,
      };

      if (selectedProductData?.customFields && selectedProductData?.productCategoryId) {
        const categoryData = await referenceDataSyncService.getProductCategoryDataById(
          selectedProductData.productCategoryId.toString()
        );

        if (categoryData && categoryData.length > 0) {
          const convertedCustomFields = await convertEpochToDayjs(
            selectedProductData.customFields,
            (categoryData[0] as any).customFields?.fields
          );

          formValues.customFields = convertedCustomFields;
        }
      }

      addProductForm.setFieldsValue(formValues);

      setIsEditDataLoading(false);
      setIsEdit(true);
    } catch (error) {
      console.error('Failed to fetch product data for editing:', error);
      errorNotification(t('productLoadFail'));
      setIsEditDataLoading(false);
    }
  };

  const handleNewProductButton = () => {
    setIsEdit(false);
    setIsProductModalOpen(true);
    setCategoryOptions([]);
    addProductForm.resetFields();
  };

  const closeNewProductModal = () => {
    addProductForm.resetFields();
    setNewProduct(initialProduct);
    setIsProductModalOpen(false);
    setIsConfirmationOpen(false);
  };

  const cancelConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  const cancelDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
  };

  if (loading) {
    return (
      <div className="accreditation-container">
        <div className="accreditation-counts">
          <div className="total-accreditation-container">
            <div className="total-accreditation total-back-bg">
              <div className="total-accreditation-text">
                <span>{t('loadingProducts')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {isPresentationModalOpen && (
        <PresentationsDisplay
          t={t}
          defaultPageSize={10}
          productId={Number(selectedProductId)}
          onClose={() => {
            setIsPresentationModalOpen(false);
          }}
          open={isPresentationModalOpen}
          productName={selectedProductName}
        />
      )}
      <ConfirmModelComponent
        isOpen={isConfirmationOpen}
        onClickedCancel={cancelConfirmation}
        header={isEdit ? t('confirmUpdateTitle') : t('confirmCreateTitle')}
        content={<p>{isEdit ? t('confirmUpdateMessage') : t('confirmCreateMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleCreate,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelConfirmation,
          },
        }}
        styleClasses="z-100"
      />

      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={cancelDeleteConfirmation}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleDelete,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDeleteConfirmation,
          },
        }}
        styleClasses="z-100"
      />

      <ConfirmModelComponent
        isOpen={isRestoreConfirmationOpen}
        onClickedCancel={cancelRestoreConfirmation}
        header={t('confirmRestoreTitle')}
        content={<p>{t('confirmRestoreMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleRestore,
            isDisabled: isConfirmClicked,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelRestoreConfirmation,
          },
        }}
        styleClasses="z-100"
      />

      <FormModelComponent
        isOpen={isProductModalOpen}
        header={isEdit ? t('updateProductTitle') : t('createProductTitle')}
        subheader={isEdit ? t('updateProductSubTitle') : t('createProductSubTitle')}
        content={
          <AddProductForm
            form={addProductForm}
            // product={newProduct}
            // handleCreate={handleCreate}
            handleCancel={handleCancel}
            getConfirmation={getConfirmation}
            isModal={true}
            isEdit={isEdit}
            initialCategoryOptions={categoryOptions}
            isEditDataLoading={isEditDataLoading}
            productId={selectedProductId}
          />
        }
        onClickedCancel={closeNewProductModal}
        footerAction={{
          confimation: {
            label: `${isEdit ? t('updateProduct') : t('createProduct')}`,

            onClick: getConfirmation,
          },
          cancel: {
            label: t('cancel'),

            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[60vh] md:max-h-[70vh] overflow-y-auto space-y-2"
      ></FormModelComponent>

      <div className="mt-4 flex w-full flex-col justify-start gap-4 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h2 className="text-xl font-medium text-headings-font-color">
            {t('productMainHeading')}
          </h2>
          <p className="text-base font-medium text-font-color">{t('productSubHeading')}</p>
        </div>

        {canCreatePresentations && (
          <div className="flex justify-end">
            <PrimarySquareBtn handleOnClick={handleNewProductButton}>
              <div className="flex">
                <Plus className="h-6 w-6 text-white" />
                <p className="pt-1">{t('newProduct')}</p>
              </div>
            </PrimarySquareBtn>
          </div>
        )}
      </div>

      {/* filter section */}
      <div className="my-6">
        {/* <BasicCard> */}
        {/* <div className="p-4"> */}
        <section className="mb-8 w-full">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
              <Button
                type="link"
                onClick={clearAllFilters}
                className="hover:text-primary-dark text-sm text-primary"
                disabled={!enableClearFiltersBtn}
              >
                {t('clearFilters')}
              </Button>
            </div>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('baskets')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('basketsFilterPlaceholder')}
                  mode="multiple"
                  allowClear
                  maxTagCount={1}
                  maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                  onClear={() => setSelectedBaskets([])}
                  value={selectedBaskets}
                  onChange={setSelectedBaskets}
                  loading={isLoadingFilterData}
                  options={baskets?.map((basket: any) => ({
                    label: basket.name,
                    value: basket.id,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">{t('products')}</label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('productFilterPlaceholder')}
                  mode="multiple"
                  maxTagCount={1}
                  maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                  allowClear
                  onClear={() => setSelectedProducts([])}
                  value={selectedProducts}
                  onChange={setSelectedProducts}
                  loading={isLoadingFilterData}
                  options={productFilterData?.map((product: any) => ({
                    label: product.name,
                    value: product.refId,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('productCategories')}
                  </label>
                </div>
                <Select
                  className="w-full [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('categoriesFilterPlaceholder')}
                  mode="multiple"
                  maxTagCount={1}
                  maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 15 : 10}
                  allowClear
                  onClear={() => setSelectedProductCategories([])}
                  value={selectedProductCategories}
                  onChange={setSelectedProductCategories}
                  loading={isLoadingFilterData}
                  options={productCategories.map((category: any) => ({
                    label: `${category.mainCategory}${category.category ? ` > ${category.category}` : ''}`,
                    value: category.id.toString(),
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    normalize(option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(normalize(input.toLowerCase()))
                  }
                />
              </Col>

              <Col xs={24} sm={12} md={8} lg={5}>
                <div className="mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('statusFilterLabel')}
                  </label>
                </div>
                <div title={!isOnline ? t('offlineStatusInfoTitle') : undefined}>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    value={statusFilter}
                    disabled={!isOnline}
                    onChange={(val: ActiveFilterEnum) => {
                      setStatusFilter(val);
                      setCurrentPage(1);
                    }}
                    options={Object.values(ActiveFilterEnum).map((status) => ({
                      label: t(`common:statusFilter.${status}`),
                      value: status,
                    }))}
                  />
                </div>
              </Col>
            </Row>
          </div>
        </section>

        <div className="my-6 w-full">
          <ProductList
            products={productData}
            getDeleteConfirmation={getDeleteConfirmation}
            getRestoreConfirmation={getRestoreConfirmation}
            getPresentationDetails={getPresentationDetails}
            handleEdit={handleEdit}
            totalProducts={totalProducts}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={isLoadingProducts}
            statusFilter={statusFilter}
          />
        </div>
        {/* </div> */}
        {/* </BasicCard> */}
      </div>

      <div className="accreditation-container">
        <div className="accreditation-counts">
          {!authStatus.isAuthenticated && (
            <Alert
              message="Authentication Required"
              description="You need to be logged in to sync data with the server. Please log in to enable sync functionality."
              type="warning"
              showIcon
              style={{ marginBottom: '20px' }}
            />
          )}

          {/*  {error && (
            <div className="accreditation-by-status-container back-bg rejected-bg">
              <span className="accreditation-by-status-text">Error: {error}</span>
            </div>
          )}

          {showSuccess && (
            <div className="accreditation-by-status-container back-bg accredited-bg">
              <span className="accreditation-by-status-text">✅ Product added successfully!</span>
            </div>
          )} */}
        </div>
      </div>
    </>
  );
};
/* 
const ProductView = () => {
  return <Product />;
}; */

export default ProductView;
